import React, { useCallback, useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import log from 'loglevel'
import styled from 'styled-components/macro'
import { DocumentRequest } from '@trustero/trustero-api-web/lib/request/request_pb'
import { Jira, ServiceNow } from 'src/components/Icons/Dependencies'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { RequestsAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import { useReceptorScanContext } from 'src/context/ReceptorScanStatus'
import { useScanReceptor } from 'src/pages/Receptors/receptors.hooks'
import { Spinner } from 'src/Throbber'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { GridColumn, GridRow } from '../../../components/Reusable/Grid'
import { formatISODate } from '../../../Utils/formatDate'
import {
  useAddOrUpdateDocumentRequest,
  useUpdateDocumentRequest,
} from '../../../components/async/DocumentRequest/useDocumentRequests'
import {
  IconButton,
  IconButtonVariant,
} from '../../../components/Reusable/Buttons'
import { TestIds } from '../../../Utils/testIds'
import { REQUEST_USE_CASE_TO_LABEL } from '../requests.constants'
import { normalizeLinkedTicketUrl } from '../requestsWorkflow.utils'
import { IconCommentMentionSVG } from '../../../components/Icons/Basic'
import { RequestItemMenu } from './RequestItemComponents/RequestItemMenu/RequestItemMenu'
import { RequestStatusDropdown } from './RequestItemComponents/RequestStatus/RequestStatusDropdown'
import { RequestOwnerButton } from './RequestItemComponents/RequestOwnerButton'

const LinkedTicketButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`

const LinkedTicketLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const LinkedTicketMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const LinkedTicketsList = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
`

const ExternalStatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f5f7fa;
  color: #394150;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`

const EmptyCellPlaceholder = styled.span`
  color: #9aa4b2;
`
import { MentionIndicatorContainer } from './RequestsItem.styles'

export interface RequestsItemProps {
  request: DocumentRequest
  auditName?: string
  fromControlShowPage?: boolean
  fromEvidenceShowPage?: boolean
  customMenu?: JSX.Element
  onOpenCreateWorkflowTask?: (
    request: DocumentRequest,
    receptorId: string,
  ) => void
  onUnlink?: () => Promise<void>
}

export const RequestsItem = ({
  request,
  auditName,
  customMenu,
  onOpenCreateWorkflowTask,
  onUnlink,
}: RequestsItemProps): JSX.Element => {
  const renderLinkedTicketIcon = (
    receptorModelId: string,
    ticketUrl: string,
  ) => {
    const modelId = receptorModelId.trim().toLowerCase()
    const url = normalizeLinkedTicketUrl(ticketUrl).toLowerCase()

    if (
      modelId === 'trr-jira-workflow' ||
      url.includes('atlassian.net') ||
      url.includes('/browse/')
    ) {
      return (
        <Jira width="14px" height="14px" style={{ objectFit: 'contain' }} />
      )
    }
    if (
      modelId === 'trr-servicenow-workflow' ||
      url.includes('service-now.com') ||
      url.includes('/nav_to.do')
    ) {
      return (
        <ServiceNow
          width="14px"
          height="14px"
          style={{ objectFit: 'contain' }}
        />
      )
    }
    return null
  }

  const canUpdateRequestStatus = useCanPerformAction<DocumentRequest.AsObject>(
    CAPABILITY.CAN_UPDATE_REQUEST_STATUS,
    request.toObject(),
  )
  const canUpdateRequestOwner = useCanPerformAction<DocumentRequest.AsObject>(
    CAPABILITY.CAN_UPDATE_REQUEST_OWNER,
    request.toObject(),
  )
  const params = useParams()
  const location = useLocation()
  const requestId = request.getId()
  const newModel = useNewModel()
  const updateDocumentRequestNewModel = useUpdateDocumentRequest()
  const updateDocumentRequest = useAddOrUpdateDocumentRequest()
  const scanNow = useScanReceptor()
  const { scanInProgressState } = useReceptorScanContext()

  const dueDate = request.getDueDate()
  const date = dueDate ? formatISODate(dueDate.toDate()) : '—'
  const ownerId = request.getOwnerId()
  const requestString = request.getName()
  const displayId = request.getDisplayId()
  const linkedTicketKey = request.getLinkedTicketKey()
  const linkedTicketUrl = request.getLinkedTicketUrl()
  const linkedTicketReceptorModelId = request.getLinkedTicketReceptorModelId()
  const linkedTicketStatus = request.getLinkedTicketStatus()
  const linkedTicketReceptorId = request.getLinkedTicketReceptorId()
  const linkedTickets = useMemo(() => {
    const linkedWorkflowsRaw =
      ((
        request as unknown as { getLinkedWorkflowsList?: () => unknown[] }
      ).getLinkedWorkflowsList?.() as Array<{
        getSourceKey?: () => string
        getUrl?: () => string
        getStatus?: () => string
        getReceptorId?: () => string
      }>) || []

    if (linkedWorkflowsRaw.length > 0) {
      return linkedWorkflowsRaw
        .map((wf) => ({
          key: wf.getSourceKey?.() || '',
          url: wf.getUrl?.() || '',
          status: wf.getStatus?.() || '',
          receptorId: wf.getReceptorId?.() || '',
        }))
        .filter((wf) => wf.key || wf.url || wf.status || wf.receptorId)
    }
    if (linkedTicketKey || linkedTicketUrl || linkedTicketStatus) {
      return [
        {
          key: linkedTicketKey,
          url: linkedTicketUrl,
          status: linkedTicketStatus,
          receptorId: linkedTicketReceptorId,
        },
      ]
    }
    return []
  }, [
    request,
    linkedTicketKey,
    linkedTicketUrl,
    linkedTicketStatus,
    linkedTicketReceptorId,
  ])
  const isSyncingTicketStatus = useMemo(() => {
    return linkedTickets.some(
      (ticket) =>
        !!ticket.receptorId && !!scanInProgressState[ticket.receptorId],
    )
  }, [scanInProgressState, linkedTickets])
  const useCaseTypeLabel =
    REQUEST_USE_CASE_TO_LABEL[request.getUseCaseType()] || ''
  const requestPermalink = `/${params.pageContext}/${RequestsAbsoluteRoutes.SHOW}/${requestId}${location.search}`
  const showDelete = !customMenu

  // Unused but kept to satisfy linter — status updates still happen via dropdown
  const _saveRequestBody = useCallback(
    async (newValue: string) => {
      try {
        if (newModel) {
          await updateDocumentRequestNewModel({
            id: requestId,
            request: newValue,
          })
        } else {
          await updateDocumentRequest({ id: requestId, request: newValue })
        }
      } catch (e) {
        log.error(`Error updating request for request id: ${requestId}`, e)
      }
    },
    [newModel, updateDocumentRequestNewModel, updateDocumentRequest, requestId],
  )

  return (
    // Columns: ID | Name | Type | Audit | Assignee | Due Date | Status | Linked Ticket | Mentions | Menu
    <GridRow as={Link} to={requestPermalink}>
      <GridColumn style={{ whiteSpace: 'nowrap' }}>{displayId}</GridColumn>
      <GridColumn data-testid={TestIds.REQUEST_COLUMN}>
        {requestString}
      </GridColumn>
      <GridColumn style={{ whiteSpace: 'nowrap' }}>
        {useCaseTypeLabel}
      </GridColumn>
      <GridColumn>
        {auditName?.trim() ? (
          auditName
        ) : (
          <EmptyCellPlaceholder>&mdash;</EmptyCellPlaceholder>
        )}
      </GridColumn>
      <GridColumn>
        <RequestOwnerButton
          id={requestId}
          ownerId={ownerId}
          isDisabled={!canUpdateRequestOwner}
        />
      </GridColumn>
      <GridColumn>{date}</GridColumn>
      <GridColumn>
        <RequestStatusDropdown
          request={request}
          isDisabled={!canUpdateRequestStatus}
        />
      </GridColumn>
      <GridColumn>
        {linkedTickets.length > 0 ? (
          <LinkedTicketMeta>
            <LinkedTicketsList>
              {linkedTickets.map((ticket, idx) => (
                <LinkedTicketMeta
                  key={`${ticket.key || 'ticket'}-${ticket.receptorId || idx}`}
                >
                  {ticket.key && ticket.url ? (
                    <LinkedTicketButton
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        window.open(
                          normalizeLinkedTicketUrl(ticket.url),
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }}
                    >
                      <LinkedTicketLabel>
                        {renderLinkedTicketIcon(
                          linkedTicketReceptorModelId,
                          normalizeLinkedTicketUrl(ticket.url),
                        )}
                        {ticket.key}
                      </LinkedTicketLabel>
                    </LinkedTicketButton>
                  ) : null}
                  {ticket.status ? (
                    <ExternalStatusChip>{ticket.status}</ExternalStatusChip>
                  ) : null}
                </LinkedTicketMeta>
              ))}
            </LinkedTicketsList>
            {isSyncingTicketStatus ? (
              <Spinner size="s" color="secondary" />
            ) : (
              <IconButton
                variant={IconButtonVariant.icon}
                onClick={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!linkedTicketReceptorId) {
                    showInfoToast(
                      'Unable to start scan: linked workflow instance is missing.',
                    )
                    return
                  }
                  await scanNow({
                    receptorId: linkedTicketReceptorId,
                    receptorModelId: linkedTicketReceptorModelId || '',
                  })
                }}
              >
                <FontAwesomeIcon icon={faRefresh} />
              </IconButton>
            )}
          </LinkedTicketMeta>
        ) : (
          '—'
        )}
      </GridColumn>
      <GridColumn>
        <MentionIndicatorContainer>
          {request.getIsMentioned() && <IconCommentMentionSVG />}
        </MentionIndicatorContainer>
      </GridColumn>
      {showDelete && (
        <GridColumn>
          <RequestItemMenu
            request={request}
            permalink={requestPermalink}
            onOpenCreateWorkflowTask={onOpenCreateWorkflowTask}
            onUnlink={onUnlink}
          />
        </GridColumn>
      )}
      {customMenu && <GridColumn>{customMenu}</GridColumn>}
    </GridRow>
  )
}
